import React, { useRef, useEffect, useCallback, useMemo } from 'react';
import type { BrakingLapEntry } from '@/types/telemetry';
import { useI18n } from '@/context/I18nContext';

interface BrakingChartProps {
  activeLap: BrakingLapEntry | null;
  comparisonLap?: BrakingLapEntry | null;
  hoveredSampleIndex?: number | null;
  onHoverSample?: (index: number | null) => void;
  height?: number;
}

export function BrakingChart({
  activeLap,
  comparisonLap,
  hoveredSampleIndex,
  onHoverSample,
  height = 240,
}: BrakingChartProps) {
  const { t } = useI18n();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Map backend-enriched samples for chart rendering
  const chartData = useMemo(() => {
    if (!activeLap?.samples || activeLap.samples.length === 0) return [];
    const baseDist = activeLap.samples[0].distanceM ?? 0;
    return activeLap.samples.map((s, idx) => {
      const dist = Math.max(0, (s.distanceM ?? 0) - baseDist);
      const speed = s.speedKmh ?? 0;
      const gLat = Math.abs(s.gLat ?? 0);
      const gLong = s.gLong ?? 0;
      const gTotal = s.gTotal ?? 0;
      return { idx, dist, speed, gLat, gLong, gTotal, sample: s };
    });
  }, [activeLap]);

  // Comparison lap data mapped
  const compData = useMemo(() => {
    if (!comparisonLap?.samples || comparisonLap.samples.length === 0) return [];
    const baseDist = comparisonLap.samples[0].distanceM ?? 0;
    return comparisonLap.samples.map((s, idx) => {
      const dist = Math.max(0, (s.distanceM ?? 0) - baseDist);
      const speed = s.speedKmh ?? 0;
      return { idx, dist, speed };
    });
  }, [comparisonLap]);

  const minSpeedIdx = activeLap?.minSpeedIndex ?? -1;

  // Dimensions & bounds
  const totalDist = useMemo(() => {
    if (chartData.length === 0) return 100;
    return Math.max(20, chartData[chartData.length - 1].dist);
  }, [chartData]);

  const maxSpeed = useMemo(() => {
    let sMax = 80;
    chartData.forEach(d => { sMax = Math.max(sMax, d.speed); });
    compData.forEach(d => { sMax = Math.max(sMax, d.speed); });
    return Math.ceil(sMax / 10) * 10 + 10;
  }, [chartData, compData]);

  // Render Canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    const w = rect.width;
    const h = rect.height;

    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    ctx.resetTransform();
    ctx.scale(dpr, dpr);

    const padLeft = 45;
    const padRight = 45;
    const padTop = 15;
    const padBottom = 25;
    const plotW = w - padLeft - padRight;
    const plotH = h - padTop - padBottom;

    ctx.clearRect(0, 0, w, h);

    if (chartData.length === 0) {
      ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
      ctx.font = '12px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(t('common.noData') || 'No telemetry data', w / 2, h / 2);
      return;
    }

    const toX = (dist: number) => padLeft + (dist / totalDist) * plotW;
    const toSpeedY = (speed: number) => padTop + plotH - (speed / maxSpeed) * plotH;
    // G scale: -2.5 to +2.5
    const toGY = (g: number) => padTop + plotH / 2 - (g / 2.5) * (plotH / 2);

    // 1. Grid lines and axes
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.05)';
    ctx.lineWidth = 1;
    ctx.beginPath();

    // Speed grid lines
    for (let s = 20; s <= maxSpeed; s += 20) {
      const y = toSpeedY(s);
      ctx.moveTo(padLeft, y);
      ctx.lineTo(w - padRight, y);
    }
    // Distance grid lines
    const distStep = totalDist > 150 ? 50 : totalDist > 60 ? 20 : 10;
    for (let d = 0; d <= totalDist; d += distStep) {
      const x = toX(d);
      ctx.moveTo(x, padTop);
      ctx.lineTo(x, padTop + plotH);
    }
    ctx.stroke();

    // Zero G reference line
    const zeroGY = toGY(0);
    ctx.beginPath();
    ctx.moveTo(padLeft, zeroGY);
    ctx.lineTo(w - padRight, zeroGY);
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)';
    ctx.setLineDash([2, 2]);
    ctx.stroke();
    ctx.setLineDash([]);

    // Axis Labels
    ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
    ctx.font = '9px monospace';
    ctx.textAlign = 'right';
    for (let s = 0; s <= maxSpeed; s += 30) {
      ctx.fillText(`${s}`, padLeft - 6, toSpeedY(s) + 3);
    }
    ctx.textAlign = 'left';
    ctx.fillText('+2G', w - padRight + 6, toGY(2.0) + 3);
    ctx.fillText('0G', w - padRight + 6, zeroGY + 3);
    ctx.fillText('-2G', w - padRight + 6, toGY(-2.0) + 3);

    // X Axis distance ticks
    ctx.textAlign = 'center';
    for (let d = 0; d <= totalDist; d += distStep) {
      ctx.fillText(`${d}m`, toX(d), h - 8);
    }

    // 2. Braking onset vertical line
    const brakeIdx = activeLap?.brakeStartIndex;
    if (brakeIdx != null && brakeIdx >= 0 && brakeIdx < chartData.length && minSpeedIdx !== brakeIdx) {
      const brakeX = toX(chartData[brakeIdx].dist);
      ctx.beginPath();
      ctx.moveTo(brakeX, padTop);
      ctx.lineTo(brakeX, padTop + plotH);
      ctx.strokeStyle = 'rgba(239, 68, 68, 0.6)';
      ctx.setLineDash([3, 3]);
      ctx.stroke();
      ctx.setLineDash([]);

      ctx.fillStyle = '#ef4444';
      ctx.font = '9px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('BRAKE', brakeX, padTop + 10);
    }

    // 2.5 Minimum speed vertical line
    if (minSpeedIdx >= 0 && minSpeedIdx < chartData.length) {
      const minX = toX(chartData[minSpeedIdx].dist);
      ctx.beginPath();
      ctx.moveTo(minX, padTop);
      ctx.lineTo(minX, padTop + plotH);
      ctx.strokeStyle = 'rgba(6, 182, 212, 0.5)';
      ctx.setLineDash([3, 3]);
      ctx.stroke();
      ctx.setLineDash([]);

      ctx.fillStyle = '#06b6d4';
      ctx.font = '9px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('MIN SPEED', minX, padTop + 10);
    }

    // 3. Comparison Lap Speed (if present)
    if (compData.length > 1) {
      ctx.beginPath();
      compData.forEach((d, i) => {
        const x = toX(d.dist);
        const y = toSpeedY(d.speed);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.strokeStyle = '#a855f7';
      ctx.lineWidth = 1.8;
      ctx.setLineDash([4, 3]);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // 4. Longitudinal G trace (blue)
    ctx.beginPath();
    chartData.forEach((d, i) => {
      const x = toX(d.dist);
      const y = toGY(d.gLong);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.strokeStyle = '#60a5fa';
    ctx.lineWidth = 1.8;
    ctx.stroke();

    // 5. Lateral G trace (red)
    ctx.beginPath();
    chartData.forEach((d, i) => {
      const x = toX(d.dist);
      const y = toGY(d.gLat);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.strokeStyle = '#f87171';
    ctx.lineWidth = 1.8;
    ctx.stroke();

    // 6. Total G trace (purple)
    ctx.beginPath();
    chartData.forEach((d, i) => {
      const x = toX(d.dist);
      const y = toGY(d.gTotal);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.strokeStyle = '#c084fc';
    ctx.lineWidth = 1.2;
    ctx.setLineDash([2, 2]);
    ctx.stroke();
    ctx.setLineDash([]);

    // 7. Active Lap Speed trace (green)
    ctx.beginPath();
    chartData.forEach((d, i) => {
      const x = toX(d.dist);
      const y = toSpeedY(d.speed);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.strokeStyle = '#34d399';
    ctx.lineWidth = 2.5;
    ctx.stroke();

    // 8. Hover cursor line & indicator
    if (hoveredSampleIndex != null && hoveredSampleIndex >= 0 && hoveredSampleIndex < chartData.length) {
      const target = chartData[hoveredSampleIndex];
      const hx = toX(target.dist);

      ctx.beginPath();
      ctx.moveTo(hx, padTop);
      ctx.lineTo(hx, padTop + plotH);
      ctx.strokeStyle = '#38bdf8';
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // Dot on speed trace
      ctx.beginPath();
      ctx.arc(hx, toSpeedY(target.speed), 4, 0, Math.PI * 2);
      ctx.fillStyle = '#34d399';
      ctx.fill();
      ctx.stroke();

      // Dot on Glong trace
      ctx.beginPath();
      ctx.arc(hx, toGY(target.gLong), 4, 0, Math.PI * 2);
      ctx.fillStyle = '#60a5fa';
      ctx.fill();
      ctx.stroke();

      // Dot on Glat trace
      ctx.beginPath();
      ctx.arc(hx, toGY(target.gLat), 4, 0, Math.PI * 2);
      ctx.fillStyle = '#f87171';
      ctx.fill();
      ctx.stroke();
    }
  }, [chartData, compData, totalDist, maxSpeed, minSpeedIdx, hoveredSampleIndex, t]);

  // Handle Mouse Scrubbing
  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas || chartData.length === 0) return;

    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const padLeft = 45;
    const padRight = 45;
    const plotW = rect.width - padLeft - padRight;

    if (mx < padLeft || mx > rect.width - padRight) {
      onHoverSample?.(null);
      return;
    }

    const mouseDist = ((mx - padLeft) / plotW) * totalDist;

    // Find nearest sample
    let bestDistDiff = Infinity;
    let bestIdx = 0;
    chartData.forEach((d, i) => {
      const diff = Math.abs(d.dist - mouseDist);
      if (diff < bestDistDiff) {
        bestDistDiff = diff;
        bestIdx = i;
      }
    });

    onHoverSample?.(bestIdx);
  }, [chartData, totalDist, onHoverSample]);

  const handleMouseLeave = useCallback(() => {
    onHoverSample?.(null);
  }, [onHoverSample]);

  return (
    <div ref={containerRef} className="card p-4 flex flex-col relative">
      {/* Header */}
      <div className="w-full flex items-center justify-between mb-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-text-primary">
          {t('braking.telemetryChart')}
        </span>

        {/* Trace Legends */}
        <div className="flex items-center gap-3 text-[11px] font-mono">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-1 rounded-full bg-[#34d399] inline-block" />
            <span className="text-text-secondary">{t('braking.speed')} (km/h)</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-1 rounded-full bg-[#60a5fa] inline-block" />
            <span className="text-text-secondary">{t('braking.gLong')} (G)</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-1 rounded-full bg-[#f87171] inline-block" />
            <span className="text-text-secondary">{t('braking.gLat')} (G)</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-1 rounded-full bg-[#c084fc] inline-block" />
            <span className="text-text-secondary">{t('braking.gTotal')} (G)</span>
          </div>
          {comparisonLap && (
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-1 rounded-full bg-[#a855f7] inline-block border-t border-dashed" />
              <span className="text-purple-400">{comparisonLap.driverName}</span>
            </div>
          )}
        </div>
      </div>

      {/* Chart Canvas */}
      <div className="relative w-full">
        <canvas
          ref={canvasRef}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          className="cursor-crosshair w-full"
          style={{ height: `${height}px` }}
        />
      </div>
    </div>
  );
}
